import axios from 'axios';
import { clearAuthStorage } from './auth';

const localApiPrefix = 'http://localhost:5000';
const browserOrigin =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : null;
const defaultApiBaseUrl =
  process.env.REACT_APP_API_BASE_URL ||
  (browserOrigin && !/localhost|127\.0\.0\.1/i.test(browserOrigin)
    ? browserOrigin
    : localApiPrefix);

function normalizeApiPath(pathname) {
  if (!pathname || pathname === '/') {
    return '/api';
  }

  return pathname.startsWith('/api') ? pathname : `/api${pathname}`;
}

function toRelativeApiUrl(url) {
  if (typeof url !== 'string' || !url) {
    return url;
  }

  if (url.startsWith('api/')) {
    return `/${url}`;
  }

  if (url.startsWith('/api')) {
    return url;
  }

  if (url.startsWith(localApiPrefix)) {
    const parsed = new URL(url);
    return `${normalizeApiPath(parsed.pathname)}${parsed.search}${parsed.hash}`;
  }

  return url;
}

axios.defaults.baseURL = defaultApiBaseUrl;

function withBearerToken(token) {
  if (!token) {
    return token;
  }

  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function normalizeRole(role) {
  return String(role || '').trim().toLowerCase();
}

function getCurrentTenantId() {
  const tenantId = localStorage.getItem('tenantId');
  return tenantId === undefined || tenantId === null || tenantId === '' ? null : tenantId;
}

function isCurrentSessionAdmin() {
  return localStorage.getItem('isSuperUser') === 'true';
}

function shouldInjectTenant(config) {
  const method = String(config?.method || 'get').toLowerCase();
  return ['post', 'put', 'patch', 'delete'].includes(method);
}

function injectTenantIntoPayload(data, tenantId) {
  if (!data || !tenantId) {
    return data;
  }

  if (data instanceof FormData) {
    if (!data.has('tenantId')) {
      data.append('tenantId', tenantId);
    }
    return data;
  }

  if (typeof data === 'object' && !Array.isArray(data)) {
    if (data.tenantId === undefined || data.tenantId === null || data.tenantId === '') {
      return { ...data, tenantId };
    }
  }

  return data;
}

axios.interceptors.request.use((config) => {
  if (config?.url) {
    config.url = toRelativeApiUrl(config.url);
  }

  const token = localStorage.getItem('token');
  const existingAuthorization = config.headers?.Authorization || config.headers?.authorization;

  if (existingAuthorization) {
    config.headers = {
      ...config.headers,
      Authorization: withBearerToken(existingAuthorization),
    };
  } else if (token) {
    config.headers = {
      ...config.headers,
      Authorization: withBearerToken(token),
    };
  }

  const tenantId = getCurrentTenantId();
  if (tenantId && !isCurrentSessionAdmin() && shouldInjectTenant(config)) {
    config.data = injectTenantIntoPayload(config.data, tenantId);
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error?.config?.url || '';
    const isLoginRequest = typeof requestUrl === 'string' && requestUrl.includes('/api/login');
    const isTimeClockRequest =
      typeof requestUrl === 'string' &&
      (
        requestUrl.includes('/api/time-clock/login') ||
        requestUrl.includes('/api/time-clock/face-login') ||
        requestUrl.includes('/api/time-clock/punch')
      );

    if (error?.response?.status === 401 && !isLoginRequest && !isTimeClockRequest) {
      clearAuthStorage();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

const originalFetch = window.fetch.bind(window);
window.fetch = (input, init) => {
  const token = localStorage.getItem('token');
  const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', withBearerToken(token));
  } else if (headers.has('Authorization')) {
    headers.set('Authorization', withBearerToken(headers.get('Authorization')));
  }

  if (typeof input === 'string') {
    return originalFetch(toRelativeApiUrl(input), {
      ...init,
      headers,
    });
  }

  if (input instanceof Request) {
    return originalFetch(
      new Request(toRelativeApiUrl(input.url), {
        method: input.method,
        headers,
        body: input.body,
        mode: input.mode,
        credentials: input.credentials,
        cache: input.cache,
        redirect: input.redirect,
        referrer: input.referrer,
        referrerPolicy: input.referrerPolicy,
        integrity: input.integrity,
        keepalive: input.keepalive,
        signal: input.signal,
      }),
      init
    );
  }

  return originalFetch(input, {
    ...init,
    headers,
  });
};

export { toRelativeApiUrl };

